import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Sparkles, Copy, Check, Phone, MessageSquareReply, CalendarPlus,
  CheckCircle2, XCircle, Clock, TrendingDown, Wallet, Users2, ClipboardList, Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import InterventionDialog from "@/components/InterventionDialog";
import { api } from "@/api";
import { formatINR, ScoreRing, RiskBadge } from "@/lib/risk";
import { toast } from "sonner";

const PIPELINE = ["Risk Detected", "Parent Contacted", "Parent Responded", "Intervention Scheduled", "Monitoring", "Recovered"];
const SIGNAL_COLOR = { attendance: "#0EA5E9", academic: "#EF4444", homework: "#F59E0B", fee: "#8B5CF6", parent: "#64748B" };

function DeltaCard({ icon: Icon, label, value, prev, prevLabel, tone = "down" }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4 card-shadow">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl font-bold text-ink">{value}</div>
      <div className="mt-1 flex items-center gap-1 text-xs text-ink-muted">
        {tone === "down" && <TrendingDown className="h-3.5 w-3.5 text-risk-high" />}
        {prevLabel || "Previous"}: {prev}
      </div>
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [pm, setPm] = useState(null);
  const [pmLoading, setPmLoading] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ivOpen, setIvOpen] = useState(false);

  const load = () => api.student(id).then(setS).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!s) return <div className="py-24 text-center text-ink-muted">Loading student profile…</div>;

  const setStage = async (stage) => {
    const updated = await api.setStage(id, stage);
    setS((prev) => ({ ...prev, ...updated }));
    toast.success(`Marked: ${stage}`);
  };

  const genMessage = async () => {
    setPmOpen(true); setPmLoading(true); setPm(null);
    try {
      const res = await api.parentMessage(id);
      setPm(res);
    } catch { toast.error("Could not generate message"); }
    finally { setPmLoading(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(pm.message);
    setCopied(true); toast.success("Message copied");
    setTimeout(() => setCopied(false), 1800);
  };

  const rp = s.rescue_plan;
  const stageIdx = PIPELINE.indexOf(s.stage);
  const isHigh = s.risk_level === "High Risk";
  const isWatch = s.risk_level === "Watch";
  const isHealthy = s.risk_level === "Healthy";
  const isRecovered = s.risk_level === "Recovered";
  const isLost = s.risk_level === "Lost";
  const first = s.name.split(" ")[0];
  const breakdownHeading = isHigh
    ? "Why this student is at risk"
    : isWatch
    ? "Early signals worth monitoring"
    : "Signal overview";

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <Link to="/risk-queue" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted hover:text-ink" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Risk Queue
      </Link>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-6 rounded-2xl border border-border bg-white p-6 card-shadow-lg sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">{s.name}</h1>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-muted">
            <span>{s.grade}</span><span>·</span>
            <span>{s.course}</span><span>·</span>
            <span>{formatINR(s.monthly_fee)}/month</span><span>·</span>
            <span>Joined {s.joined}</span>
          </div>
          <div className="mt-3"><RiskBadge level={s.risk_level} /></div>
        </div>
        <ScoreRing score={s.risk_score} level={s.risk_level} />
      </motion.div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main */}
        <div className="space-y-6 lg:col-span-2">
          {/* Recovery banner */}
          {isRecovered && (
            <div className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50 p-6 card-shadow" data-testid="recovery-banner">
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-500 text-white">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div>
                  <div className="font-display text-xl font-bold text-emerald-800">Student Recovered</div>
                  <p className="mt-1 text-sm text-emerald-700">
                    {first}'s intervention is complete. {formatINR(s.monthly_fee)}/month recurring revenue protected.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                      Risk at detection: {s.detection_score ?? s.risk_score}
                    </span>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                      Current outcome: Recovered
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI summary */}
          <div className="relative overflow-hidden rounded-2xl bg-ink p-6 text-white card-shadow" data-testid="ai-summary">
            <div className="absolute -right-14 -top-14 h-48 w-48 rounded-full bg-indigo-brand/25 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 text-sm font-semibold text-indigo-200">
                <Sparkles className="h-4 w-4" /> Retention Analysis
              </div>
              <p className="mt-3 leading-relaxed text-white/90">{s.ai_summary}</p>
            </div>
          </div>

          {/* Stable state (healthy) or risk breakdown */}
          {isHealthy ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 card-shadow" data-testid="stable-card">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> Student is currently stable
              </div>
              <p className="mt-2 text-sm text-emerald-800/80">
                No meaningful retention-risk pattern is currently detected from the available engagement signals.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <span className="font-display text-3xl font-bold text-emerald-700">{s.risk_score}</span>
                <RiskBadge level="Healthy" />
              </div>
              <p className="mt-3 text-xs text-emerald-700/70">Continue normal monitoring.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-white p-6 card-shadow">
              <h3 className="font-display text-lg font-bold text-ink">{breakdownHeading}</h3>
              <p className="text-sm text-ink-muted">Risk score {s.risk_score} — based on current engagement data.</p>
              <div className="mt-4 space-y-3">
                {s.risk_breakdown.map((b) => (
                  <div key={b.label} data-testid={`breakdown-${b.label.replace(/\s/g, "-").toLowerCase()}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-ink-soft">{b.label}</span>
                      <span className="font-bold text-ink">+{b.points}<span className="text-xs font-normal text-ink-muted"> / {b.max}</span></span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${(b.points / b.max) * 100}%`, background: b.points > 0 ? "#4F46E5" : "#CBD5E1" }} />
                    </div>
                    <div className="mt-1 text-xs text-ink-muted">{b.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Signal timeline */}
          {s.signals?.length > 0 && (
            <div className="rounded-2xl border border-border bg-white p-6 card-shadow">
              <h3 className="font-display text-lg font-bold text-ink">Signal Timeline</h3>
              <p className="text-sm text-ink-muted">How the risk accumulated over time.</p>
              <div className="relative mt-5 space-y-5 pl-6">
                <span className="absolute left-[7px] top-1 h-[calc(100%-1rem)] w-px bg-border" />
                {s.signals.map((sig, i) => (
                  <div key={i} className="relative" data-testid={`timeline-${i}`}>
                    <span className="absolute -left-6 top-1 h-3.5 w-3.5 rounded-full border-2 border-white"
                      style={{ background: SIGNAL_COLOR[sig.type] || "#64748B", boxShadow: "0 0 0 3px #F1F5F9" }} />
                    <div className="text-xs font-semibold text-ink-muted">{sig.date}</div>
                    <div className="text-sm font-medium text-ink">{sig.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Engagement cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DeltaCard icon={Users2} label="Attendance" value={`${s.attendance_pct}%`} prev={`${s.prev_attendance_pct}%`} prevLabel="Previous period" />
            <DeltaCard icon={TrendingDown} label="Academic Performance" value={`${s.latest_score}%`} prev={`${s.prev_score}%`} prevLabel="Previous score" />
            <DeltaCard icon={ClipboardList} label="Homework Completion" value={`${s.homework_pct}%`} prev={`${s.prev_homework_pct}%`} prevLabel="Previous" />
            <div className="rounded-2xl border border-border bg-white p-4 card-shadow">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                <Wallet className="h-4 w-4" /> Fee
              </div>
              <div className="mt-2 font-display text-2xl font-bold text-ink">
                {s.fee_status === "Overdue" ? `${s.days_overdue} days overdue` : "Up to date"}
              </div>
              <div className="mt-1 text-xs text-ink-muted">Parent engagement: {s.parent_engagement} · last {s.last_contact_days}d ago</div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* High risk: full rescue plan */}
          {isHigh && (
          <div className="rounded-2xl border border-indigo-200 bg-indigo-light/60 p-6 card-shadow" data-testid="rescue-plan">
            <div className="flex items-center gap-2 text-sm font-semibold text-indigo-brand">
              <Target className="h-4 w-4" /> AI Recommended Rescue Plan
            </div>
            <div className="mt-4 space-y-3">
              <div className="rounded-xl bg-white/80 p-3">
                <div className="text-xs font-semibold uppercase text-ink-muted">Priority</div>
                <div className="text-sm font-bold text-risk-high">{rp.priority}</div>
              </div>
              <div className="rounded-xl bg-white/80 p-3">
                <div className="text-xs font-semibold uppercase text-ink-muted">Primary objective</div>
                <div className="text-sm font-bold text-ink">{rp.objective}</div>
              </div>
            </div>
            <ol className="mt-4 space-y-2.5">
              {rp.actions.map((a, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-ink-soft">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-brand text-[11px] font-bold text-white">{i + 1}</span>
                  {a}
                </li>
              ))}
            </ol>
            <div className="mt-4 rounded-xl border border-indigo-200 bg-white/70 p-3">
              <div className="text-xs font-semibold text-indigo-brand">Why this approach?</div>
              <p className="mt-1 text-xs leading-relaxed text-ink-muted">{rp.why}</p>
            </div>
            <Button onClick={genMessage} data-testid="generate-message-btn"
              className="mt-4 w-full gap-2 rounded-xl bg-indigo-brand font-semibold hover:bg-indigo-deep">
              <Sparkles className="h-4 w-4" /> Generate Parent Message
            </Button>
          </div>
          )}

          {/* Watch: soft monitoring guidance */}
          {isWatch && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 card-shadow" data-testid="monitor-card">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
              <Target className="h-4 w-4" /> Recommended Action
            </div>
            <p className="mt-3 text-sm font-bold text-ink">Monitor over the next 7 days</p>
            <p className="mt-2 text-sm text-amber-800/80">
              {first} is showing early signals worth watching, but no urgent intervention is needed yet.
              Keep an eye on attendance and recent scores; if they keep slipping, {first} may move into high risk.
            </p>
          </div>
          )}

          {/* Healthy: stable note */}
          {isHealthy && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 card-shadow" data-testid="stable-sidebar">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> No action needed
            </div>
            <p className="mt-2 text-sm text-emerald-800/80">
              {first} is on a healthy engagement track. Continue normal monitoring — no intervention is recommended.
            </p>
          </div>
          )}

          {/* Workflow */}
          {(isHigh || isRecovered || isLost) && (
          <div className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid="workflow">
            <h3 className="font-display text-lg font-bold text-ink">Intervention Workflow</h3>
            <div className="mt-4 space-y-2.5">
              {PIPELINE.map((st, i) => {
                const done = stageIdx >= i && stageIdx !== -1 && s.stage !== "Lost";
                const current = s.stage === st;
                return (
                  <div key={st} className="flex items-center gap-3">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                      done ? "bg-emerald-500 text-white" : current ? "bg-indigo-brand text-white" : "bg-secondary text-ink-muted"}`}>
                      {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </span>
                    <span className={`text-sm ${current ? "font-bold text-ink" : done ? "font-medium text-ink-soft" : "text-ink-muted"}`}>{st}</span>
                  </div>
                );
              })}
              {s.stage === "Lost" && (
                <div className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink-muted text-white"><XCircle className="h-3.5 w-3.5" /></span>
                  <span className="text-sm font-bold text-ink">Lost</span>
                </div>
              )}
            </div>

            {isHigh && (
            <div className="mt-5 space-y-2">
              <Button variant="outline" onClick={() => setStage("Parent Contacted")}
                data-testid="mark-contacted-btn" className="w-full justify-start gap-2 rounded-xl">
                <Phone className="h-4 w-4" /> Mark Parent Contacted
              </Button>
              <Button variant="outline" onClick={() => setStage("Parent Responded")}
                data-testid="mark-responded-btn" className="w-full justify-start gap-2 rounded-xl">
                <MessageSquareReply className="h-4 w-4" /> Mark Parent Responded
              </Button>
              <Button variant="outline" onClick={() => setIvOpen(true)}
                data-testid="schedule-intervention-btn" className="w-full justify-start gap-2 rounded-xl">
                <CalendarPlus className="h-4 w-4" /> Schedule Intervention
              </Button>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button onClick={() => setStage("Recovered")} data-testid="mark-recovered-btn"
                  className="gap-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600">
                  <CheckCircle2 className="h-4 w-4" /> Recovered
                </Button>
                <Button onClick={() => setStage("Lost")} data-testid="mark-lost-btn"
                  variant="outline" className="gap-1.5 rounded-xl text-ink-muted">
                  <XCircle className="h-4 w-4" /> Lost
                </Button>
              </div>
            </div>
            )}

            {s.interventions?.length > 0 && (
              <div className="mt-5 border-t border-border pt-4">
                <div className="text-xs font-semibold uppercase text-ink-muted">Logged interventions</div>
                {s.interventions.map((iv, i) => (
                  <div key={i} className="mt-2 flex items-center gap-2 text-sm text-ink-soft">
                    <Clock className="h-3.5 w-3.5 text-indigo-brand" />
                    <span className="font-medium text-ink">{iv.type}</span> · {iv.date}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Parent message dialog */}
      <Dialog open={pmOpen} onOpenChange={setPmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Parent Message</DialogTitle>
            <DialogDescription>
              Review and send manually — BatchPulse never contacts parents automatically.
            </DialogDescription>
          </DialogHeader>
          {pmLoading ? (
            <div className="py-10 text-center text-ink-muted">Drafting a warm, personalised message…</div>
          ) : pm ? (
            <div className="space-y-3">
              {pm.source === "ai" && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-light px-2.5 py-1 text-xs font-semibold text-indigo-brand">
                  <Sparkles className="h-3 w-3" /> AI-generated
                </div>
              )}
              <Textarea data-testid="parent-message-text" value={pm.message} readOnly className="min-h-[220px] rounded-xl text-sm leading-relaxed" />
              <div className="flex gap-2">
                <Button onClick={copy} data-testid="copy-message-btn" variant="outline" className="flex-1 gap-2 rounded-xl">
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy Message"}
                </Button>
                <Button onClick={() => { setStage("Parent Contacted"); setPmOpen(false); }}
                  data-testid="mark-contacted-from-msg" className="flex-1 gap-2 rounded-xl bg-indigo-brand hover:bg-indigo-deep">
                  <Phone className="h-4 w-4" /> Mark Contacted
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <InterventionDialog open={ivOpen} onOpenChange={setIvOpen} student={s}
        onSaved={(u) => setS((prev) => ({ ...prev, ...u }))} />
    </div>
  );
}
